// Portrait SVG stylisé : médaillon à la couleur du héros, silhouette/emblème de classe (épée, bâton, arc, symbole sacré, dague, luth), sigle.
import { useState } from "preact/hooks";
import type { ClassId } from "../../shared/types";
import "./Portrait.css";

export interface PortraitProps { classId: ClassId; color: string; sigil: string; size?: number; dead?: boolean }

type Rgb = [number, number, number];

const FALLBACK: Rgb = [110, 75, 47];
const WHITE: Rgb = [255, 255, 255];
const LABELS: Record<ClassId, string> = {
  guerrier: "Guerrier", mage: "Mage", rodeur: "Rôdeur", clerc: "Clerc", voleur: "Voleur", barde: "Barde",
};

// Géométrie partagée (viewBox 0 0 100 100).
const HEAD = { cx: 50, cy: 37, r: 13.5 };
const BUST = "M41 46 L41 56 C29 59 16 68 14 100 L86 100 C84 68 71 59 59 56 L59 46 Z";
const HOOD = "M50 7 Q40 20 33 34 Q30 42 28 52 L24 62 L76 62 L72 52 Q70 42 67 34 Q60 20 50 7 Z";
const STAR = star(30, 21, 8.2, 3.4);
const SUN_RAYS = rays(50, 37, 23.5, 31, 12);

let seq = 0;

function star(cx: number, cy: number, outer: number, inner: number): string {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    pts.push(`${(cx + r * Math.cos(a)).toFixed(2)} ${(cy + r * Math.sin(a)).toFixed(2)}`);
  }
  return `M${pts.join(" L")} Z`;
}

function rays(cx: number, cy: number, from: number, to: number, count: number): string {
  const segs: string[] = [];
  for (let i = 0; i < count; i++) {
    const a = (i * 2 * Math.PI) / count;
    const c = Math.cos(a);
    const s = Math.sin(a);
    segs.push(`M${(cx + from * c).toFixed(2)} ${(cy + from * s).toFixed(2)} L${(cx + to * c).toFixed(2)} ${(cy + to * s).toFixed(2)}`);
  }
  return segs.join(" ");
}

/** Hex #rgb ou #rrggbb ; toute autre valeur retombe sur une teinte de cuir. */
function parseColor(value: string): Rgb {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(value).trim());
  if (!m) return FALLBACK;
  const hex = m[1].length === 3 ? m[1].replace(/./g, (c) => c + c) : m[1];
  const n = parseInt(hex, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function mix(a: Rgb, b: Rgb, t: number): Rgb {
  const at = (i: 0 | 1 | 2) => Math.round(a[i] + (b[i] - a[i]) * t);
  return [at(0), at(1), at(2)];
}

const css = (c: Rgb) => `rgb(${c[0]} ${c[1]} ${c[2]})`;

/** Emblème de classe, dessiné derrière la silhouette. */
function Emblem({ classId }: { classId: ClassId }) {
  switch (classId) {
    case "guerrier":
      return (
        <g>
          <path class="portrait-shield" d="M13 30 H37 V46 C37 55 31 60 25 63 C19 60 13 55 13 46 Z" />
          <path class="portrait-cross" d="M25 34 V58 M16 42 H34" />
          <line class="portrait-metal-line" x1="66" y1="62" x2="73" y2="14" />
          <line class="portrait-gold-line" x1="60.5" y1="49" x2="75" y2="51" />
        </g>
      );
    case "mage":
      return (
        <g>
          <circle class="portrait-aura" cx="30" cy="21" r="12" />
          <line class="portrait-gold-line" x1="30" y1="25" x2="19" y2="98" />
          <path class="portrait-gold" d={STAR} />
        </g>
      );
    case "rodeur":
      return (
        <g>
          <path class="portrait-bow" d="M21 13 Q5 50 21 87" />
          <line class="portrait-bowstring" x1="21" y1="13" x2="21" y2="87" />
          <line class="portrait-metal-line" x1="22" y1="26" x2="67" y2="14" />
          <path class="portrait-metal" d="M73.3 12.3 L66.2 11.1 L67.8 16.9 Z" />
        </g>
      );
    case "clerc":
      return (
        <g>
          <circle class="portrait-aura" cx="50" cy="37" r="25" />
          <path class="portrait-rays" d={SUN_RAYS} />
          <circle class="portrait-sun" cx="50" cy="37" r="19" />
        </g>
      );
    case "voleur":
      return (
        <g>
          <path class="portrait-metal" d="M71 11 L75.5 44 L66.5 44 Z" />
          <rect class="portrait-gold" x="62" y="42.5" width="18" height="3.6" rx="1.8" />
          <rect class="portrait-gold" x="70.4" y="46" width="3.2" height="13" rx="1.4" />
          <circle class="portrait-gold" cx="72" cy="60.5" r="2.2" />
        </g>
      );
    case "barde":
      return (
        <g>
          <g transform="rotate(10 26 70)">
            <rect class="portrait-gold" x="24.5" y="12" width="3" height="50" rx="1" />
            <rect class="portrait-gold" x="22.5" y="9" width="7" height="6" rx="1.5" />
            <circle class="portrait-gold" cx="26" cy="64" r="10.5" />
            <circle class="portrait-hole" cx="26" cy="64" r="2.8" />
          </g>
          <ellipse class="portrait-gold" cx="67" cy="28" rx="3.4" ry="2.7" transform="rotate(-20 67 28)" />
          <path class="portrait-gold-line" d="M70 27 V13 q4 1 5 5" />
        </g>
      );
  }
}

/** Buste (ou capuche pour le voleur), liseré doré par-dessous, remplissage de nuit par-dessus. */
function Figure({ classId }: { classId: ClassId }) {
  if (classId === "voleur") {
    return (
      <g>
        <g class="portrait-rim-layer">
          <path class="portrait-rim" d={HOOD} />
          <path class="portrait-rim" d={BUST} />
        </g>
        <g class="portrait-figure">
          <path d={HOOD} />
          <ellipse class="portrait-face" cx="50" cy="38" rx="7" ry="9" />
          <path d={BUST} />
        </g>
      </g>
    );
  }
  return (
    <g>
      <g class="portrait-rim-layer">
        <circle class="portrait-rim" cx={HEAD.cx} cy={HEAD.cy} r={HEAD.r} />
        <path class="portrait-rim" d={BUST} />
      </g>
      <g class="portrait-figure">
        <circle cx={HEAD.cx} cy={HEAD.cy} r={HEAD.r} />
        <path d={BUST} />
      </g>
    </g>
  );
}

export function Portrait(props: PortraitProps) {
  const { classId, color, sigil, size = 56, dead = false } = props;
  const [uid] = useState(() => `portrait-${++seq}`);
  const base = parseColor(color);
  const label = LABELS[classId] ?? "Héros";

  return (
    <div
      class={dead ? "portrait portrait-dead" : "portrait"}
      role="img"
      aria-label={`Portrait : ${label}${dead ? ", mort" : ""}`}
      style={{ width: `${size}px`, height: `${size}px`, fontSize: `${size}px` }}
    >
      <svg class="portrait-svg" viewBox="0 0 100 100" aria-hidden="true">
        <defs>
          <radialGradient id={`${uid}-bg`} gradientUnits="userSpaceOnUse" cx="50" cy="36" r="62">
            <stop offset="0" style={{ stopColor: css(mix(base, WHITE, 0.35)) }} />
            <stop offset="0.5" style={{ stopColor: css(base) }} />
            <stop offset="1" style={{ stopColor: "var(--bg)" }} />
          </radialGradient>
          <clipPath id={`${uid}-clip`}>
            <circle cx="50" cy="50" r="45" />
          </clipPath>
        </defs>
        <circle cx="50" cy="50" r="46.5" fill={`url(#${uid}-bg)`} />
        <g clip-path={`url(#${uid}-clip)`}>
          <Emblem classId={classId} />
          <Figure classId={classId} />
          {dead && <circle class="portrait-veil" cx="50" cy="50" r="46.5" />}
        </g>
        <circle class="portrait-ring" cx="50" cy="50" r="46.5" />
        <circle class="portrait-ring-inner" cx="50" cy="50" r="42" />
      </svg>
      {sigil && (
        <span class="portrait-sigil" aria-hidden="true">
          <span class="portrait-sigil-glyph">{sigil}</span>
        </span>
      )}
    </div>
  );
}
